import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getCaptchaSiteKey, isCaptchaSiteKeyConfigured } from '../utils/captcha';

// Cloudflare Turnstile поддържа изричен списък от езикови кодове - извеждаме
// само поддържаните от нашето приложение, иначе оставяме 'auto' (детекция по браузър).
const TURNSTILE_SUPPORTED_LANGUAGES = ['bg', 'en', 'nl', 'tr', 'pl'];

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface CaptchaFieldProps {
  onTokenChange: (token: string) => void;
  errorText?: string;
  /** Увеличава се след неуспешен опит, за да се поиска нов еднократен токен. */
  resetKey?: number;
}

const TURNSTILE_SCRIPT_ID = 'turnstile-script';

const CaptchaField: React.FC<CaptchaFieldProps> = ({ onTokenChange, errorText, resetKey = 0 }) => {
  const { i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = useMemo(() => getCaptchaSiteKey(), []);
  const isConfigured = useMemo(() => isCaptchaSiteKeyConfigured(), []);

  const currentLanguage = i18n.language?.split('-')[0] ?? 'auto';
  const turnstileLanguage = TURNSTILE_SUPPORTED_LANGUAGES.includes(currentLanguage)
    ? currentLanguage
    : 'auto';

  // Пазим последния callback в ref, за да не пресъздаваме widget-а при всеки
  // render на родителския компонент (иначе onTokenChange е нова функция всеки
  // път и widget-ът се разрушава/пресъздава безкрайно, докато token-ът се сетне).
  const onTokenChangeRef = useRef(onTokenChange);
  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    if (!isConfigured || !containerRef.current) {
      return;
    }

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile || widgetIdRef.current) {
        return;
      }

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language: turnstileLanguage,
          callback: (token: string) => onTokenChangeRef.current(token),
          'expired-callback': () => onTokenChangeRef.current(''),
          'error-callback': () => onTokenChangeRef.current(''),
        });
      } catch (error) {
        console.error('Turnstile render failed. Disabling captcha widget.', error);
        onTokenChangeRef.current('');
      }
    };

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', renderWidget);
      renderWidget();
      return () => {
        existingScript.removeEventListener('load', renderWidget);
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', renderWidget);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', renderWidget);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [isConfigured, siteKey, turnstileLanguage]);

  useEffect(() => {
    if (!resetKey || !widgetIdRef.current || !window.turnstile) {
      return;
    }
    onTokenChangeRef.current('');
    window.turnstile.reset(widgetIdRef.current);
  }, [resetKey]);

  if (!isConfigured) {
    return null;
  }

  return (
    <Box>
      <Box ref={containerRef} />
      {errorText ? (
        <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
          {errorText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CaptchaField;

